import React from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Button,
  Label,
  InputGroup,
  Input,
  InputGroupAddon,
} from "reactstrap";
import { useFormContext, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import FormattedNumber from "../../FormattedNumber";
// import DPIStars from "./DPIStars";
import EditorFormSection from "../../EditorFormSection";
import icon_filesize from "../../../assets/images/icons/filesize.svg";

const SizeSection = (
  {
    isOpen,
    togglePanel,
    showMightBeDividedHint,
    showSmallSizeWarning,
    handleRemoveCrop,
  },
  context
) => {
  const { control, watch, setValue } = useFormContext();
  const width = watch("width");
  const height = watch("height");
  const fileRatio = watch("fileRatio");
  const keepRatio = watch("keepRatio");

  const handleWidthChange = (e) => {
    if (!e.target.value) {
      setValue("width", null);
      return;
    }
    let newWidth = parseInt(e.target.value.replace(/[^\d]+/g, "")) || 10;
    setValue("width", newWidth);
    if (keepRatio) {
      const newHeight = Math.round(newWidth * fileRatio);
      setValue("height", newHeight);
    } else {
      setValue("fileRatio", height / newWidth);
    }
  };

  const handleHeightChange = (e) => {
    if (!e.target.value) {
      setValue("height", null);
      return;
    }
    let newHeight = parseInt(e.target.value.replace(/[^\d]+/g, "")) || 10;
    setValue("height", newHeight);
    if (keepRatio) {
      const newWidth = Math.round(newHeight / fileRatio);
      setValue("width", newWidth);
    } else {
      setValue("fileRatio", newHeight / width);
    }
  };

  const { language, artwork, cropping } = useSelector((state) => ({
    language: state.i18nState.lang,
    addingToCart: state.editorShop.loading,
    artwork: state.artworks.show,
    cropping: state.artworks.cropping,
  }));

  return (
    <EditorFormSection
      help={context.t("FileEditor.dimensions.help")}
      panelName="size"
      panelTitle={context.t("Dimensions of your product")}
      open={isOpen}
      toggle={togglePanel}
    >
      <InputGroup>
        <Controller
          name="width"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className={
                isNaN(width) || width < 10
                  ? "is-invalid data-hj-allow"
                  : "data-hj-allow"
              }
              onChange={handleWidthChange}
              min={10}
              type="number"
            />
          )}
        />
        <InputGroupAddon addonType="append">x</InputGroupAddon>
        <Controller
          name="height"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className={
                isNaN(height) || height < 10
                  ? "is-invalid data-hj-allow"
                  : "data-hj-allow"
              }
              onChange={handleHeightChange}
              min={10}
              type="number"
            />
          )}
        />
        <InputGroupAddon addonType="append">mm</InputGroupAddon>
      </InputGroup>
      <div>
        <span className="text-info small">
          <FormattedNumber
            value={Math.round(width * 100) / 100}
            language={language}
            unit=""
            decimals={0}
          />
          &nbsp;x&nbsp;
          <FormattedNumber
            value={Math.round(height * 100) / 100}
            language={language}
            unit="mm"
            decimals={0}
          />
          &nbsp;=&nbsp;
          <strong>
            <FormattedNumber
              value={Math.round(width * 10) / 100}
              language={language}
              unit=""
              decimals={1}
            />
            &nbsp;x&nbsp;
            <FormattedNumber
              value={Math.round(height * 10) / 100}
              language={language}
              unit="cm"
              decimals={1}
            />
          </strong>
        </span>
        {/* <span className="ml-2 small">
          {context.t("Quality at this size:")}{" "}
          <DPIStars dpi={Math.round(width / height)} />
        </span> */}
      </div>
      <div className="col mt-2">
        <InputGroup>
          <Label className="small">
            <Controller
              name="keepRatio"
              control={control}
              render={({ field }) => (
                <Input type="checkbox" checked={field.value} {...field} />
              )}
            />
            <img
              width="24px"
              src={icon_filesize}
              alt={context.t("Keep proportions")}
              title={context.t("Keep proportions")}
            />
            {context.t("Keep proportions")}
          </Label>
        </InputGroup>
      </div>
      {artwork?.data?.attributes?.width && cropping?.active && (
        <Row>
          <Col xs={12}>
            <p className="small">
              <strong>{context.t("Design will be cropped")}</strong>
              <br />
              {context.t(
                "You are currently cropping the size of the original file. Click here to reset width and height to original file sizes."
              )}
            </p>
            <Button
              color="primary"
              size="sm"
              outline
              onClick={handleRemoveCrop}
            >
              {context.t("Disable Cropping and use Original Dimensions")}
            </Button>
          </Col>
        </Row>
      )}
      {showSmallSizeWarning && (
        <p className="small text-danger">
          ⚠️ {context.t("The minimum size for height and width is 10mm ")}
        </p>
      )}
      {showMightBeDividedHint && (
        <p className="small text-danger">
          ℹ️{" "}
          {context.t(
            "For technical reasons, your design may need to be divided into several parts, as both dimensions exceed the maximum manufacturable size. It can be installed without any visible gap."
          )}
        </p>
      )}
    </EditorFormSection>
  );
};

SizeSection.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  showSmallSizeWarning: PropTypes.bool,
  showMightBeDividedHint: PropTypes.bool,
  togglePanel: PropTypes.func.isRequired,
  changeSizeChangeActive: PropTypes.func.isRequired,
  handleRemoveCrop: PropTypes.func.isRequired,
};

SizeSection.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default SizeSection;
